/**
 * Textos de «olvidé mi contraseña», «restablecer contraseña» y «aceptar
 * invitación» en los cinco idiomas, los errores del registro que el inicio
 * de sesión y el alta comparten con ellas, más las piezas puras que usan esas
 * pantallas: errores del backend mapeados por estado (nunca se enseña su
 * inglés), validación de la contraseña, fecha con Intl y enlaces por idioma.
 *
 * El inglés define la forma: `tsc` falla si a un idioma le falta o le sobra
 * una clave. `{n}`, `{name}`… se rellenan con `fmt`.
 */
import type { Locale } from '@/lib/i18n';

export const ACCOUNT_FLOW_LOCALES = ['en', 'es', 'pt', 'de', 'fr'] as const satisfies readonly Locale[];

type Ampliar<T> = { [K in keyof T]: T[K] extends string ? string : Ampliar<T[K]> };

const NBSP = ' ';

const en = {
    shared: {
        language: 'Language',
        signIn: 'Sign in',
        backToSignIn: 'Back to sign in',
        loading: 'Loading...',
        connectionError: 'Connection error. Please try again.',
        tooManyRequests: 'Too many attempts. Please wait a few minutes before trying again.',
        accountDeactivated: 'Your account has been deactivated. Please contact your administrator.',
        passwordMismatch: 'Passwords do not match',
        passwordMinLength: 'Password must be at least 8 characters',
        passwordPolicy: 'Password must include an uppercase letter, a lowercase letter and a number, with no spaces.',
        passwordPlaceholder: 'Min. 8 characters',
        confirmPasswordPlaceholder: 'Confirm your password',
        showPassword: 'Show password',
        hidePassword: 'Hide password',
    },
    forgot: {
        title: 'Forgot password?',
        subtitle: "No worries, we'll send you a verification code to reset your password.",
        emailLabel: 'Email address',
        emailPlaceholder: 'Enter your email',
        submit: 'Send verification code',
        submitting: 'Sending code...',
        rememberPassword: 'Remember your password?',
        sentTitle: 'Check your email',
        sentBody: 'We sent a verification code to',
        redirecting: 'Redirecting to verification page...',
        visualTitle: 'Secure password reset',
        visualBody: "We'll send you a secure code to verify your identity and help you create a new password.",
        errorNotFound: 'No account found with this email address.',
        errorInvalidEmail: 'Enter a valid email address.',
        errorGeneric: "We couldn't send the code. Please try again.",
    },
    reset: {
        invalidTitle: 'Invalid Request',
        invalidBody: 'Please start the password reset process from the beginning.',
        invalidCta: 'Go to Forgot Password',
        successTitle: 'Password reset successful!',
        successBody: 'Your password has been changed successfully.',
        redirecting: 'Redirecting to sign in...',
        title: 'Reset your password',
        subtitle: 'Enter the verification code sent to',
        codeLabel: 'Verification code',
        codeCharAria: 'Character {n} of 6',
        noCode: "Didn't receive the code?",
        resend: 'Resend code',
        resendIn: 'Resend in {n}s',
        newPassword: 'New password',
        confirmPassword: 'Confirm new password',
        submit: 'Reset password',
        submitting: 'Resetting password...',
        visualTitle: 'Almost there!',
        visualBody: 'Enter the verification code and create a strong new password for your account.',
        tipsTitle: 'Password tips:',
        tip1: 'At least 8 characters long',
        tip2: 'Mix of letters, numbers & symbols',
        tip3: 'Avoid common words or patterns',
        errorCodeIncomplete: 'Please enter the 6-character verification code',
        errorCodeInvalid: 'The verification code is incorrect. Check it and try again.',
        errorCodeExpired: 'The code has expired. Request a new one with “Resend code”.',
        errorCodeUsed: 'This code has already been used. Request a new one.',
        errorGeneric: "We couldn't reset your password. Please try again.",
        errorResend: "We couldn't resend the code. Please try again.",
    },
    invite: {
        verifying: 'Verifying invitation...',
        invalidTitle: 'Invalid Invitation',
        goToSignIn: 'Go to Sign In',
        backToHome: 'Back to home',
        title: 'Accept Invitation',
        subtitle: 'Complete your account setup to join the team',
        invitedToJoin: "You've been invited to join",
        invitedBy: 'Invited by {name}',
        firstName: 'First name',
        lastName: 'Last name',
        email: 'Email',
        createPassword: 'Create password',
        confirmPassword: 'Confirm password',
        submit: 'Join Team',
        submitting: 'Creating account...',
        expiresOn: 'This invitation expires on {date}',
        expiresSoon: 'This invitation expires soon',
        alreadyHaveAccount: 'Already have an account?',
        visualTitle: 'Welcome to the team!',
        visualJoin: "You're about to join {account} on EZDocuAI.",
        visualSetup: 'Set up your password to start collaborating on translations.',
        statAiTitle: 'AI-Powered',
        statAiSub: 'Translation Tools',
        statRealtimeTitle: 'Real-time',
        statRealtimeSub: 'Collaboration',
        errorInvalidLink: 'Invalid invitation link',
        errorNotFound: 'This invitation is invalid or has already been used.',
        errorExpired: 'This invitation has expired. Ask the person who invited you to send a new one.',
        errorVerify: "We couldn't verify the invitation. Please try again.",
        errorAccept: "We couldn't accept the invitation. Please try again.",
        errorSession: "Your account is ready, but we couldn't sign you in. Please sign in.",
    },
    signup: {
        errorCodeExpired: 'The code has expired. Sign up again to get a new one.',
        errorTooManyAttempts: 'Too many incorrect attempts. Sign up again to get a new code.',
        errorNoPending: 'We found no pending sign-up for this email. Please sign up again.',
        errorFailed: "We couldn't create your account. Please try again.",
        errorInternalOnly: 'This environment is for internal accounts only.',
        errorName: 'Check your first and last name: at least 2 characters, no symbols.',
        errorCompanyName: 'Check the company name: at least 2 characters, no symbols.',
        errorGeneric: "We couldn't complete the request. Please try again.",
    },
};

export type AccountFlowsCopy = Ampliar<typeof en>;

const es: AccountFlowsCopy = {
    shared: {
        language: 'Idioma',
        signIn: 'Iniciar sesión',
        backToSignIn: 'Volver a iniciar sesión',
        loading: 'Cargando...',
        connectionError: 'Error de conexión. Inténtalo de nuevo.',
        tooManyRequests: 'Demasiados intentos. Espera unos minutos antes de volver a intentarlo.',
        accountDeactivated: 'Tu cuenta ha sido desactivada. Contacta a tu administrador.',
        passwordMismatch: 'Las contraseñas no coinciden',
        passwordMinLength: 'La contraseña debe tener al menos 8 caracteres',
        passwordPolicy: 'La contraseña debe incluir una mayúscula, una minúscula y un número, sin espacios.',
        passwordPlaceholder: 'Mín. 8 caracteres',
        confirmPasswordPlaceholder: 'Confirma tu contraseña',
        showPassword: 'Mostrar contraseña',
        hidePassword: 'Ocultar contraseña',
    },
    forgot: {
        title: '¿Olvidaste tu contraseña?',
        subtitle: 'No te preocupes: te enviaremos un código de verificación para restablecer tu contraseña.',
        emailLabel: 'Correo electrónico',
        emailPlaceholder: 'Ingresa tu correo',
        submit: 'Enviar código de verificación',
        submitting: 'Enviando código...',
        rememberPassword: '¿Recordaste tu contraseña?',
        sentTitle: 'Revisa tu correo',
        sentBody: 'Enviamos un código de verificación a',
        redirecting: 'Te llevamos a la página de verificación...',
        visualTitle: 'Restablecimiento seguro de contraseña',
        visualBody: 'Te enviaremos un código seguro para verificar tu identidad y ayudarte a crear una nueva contraseña.',
        errorNotFound: 'No encontramos ninguna cuenta con este correo.',
        errorInvalidEmail: 'Ingresa un correo electrónico válido.',
        errorGeneric: 'No pudimos enviar el código. Inténtalo de nuevo.',
    },
    reset: {
        invalidTitle: 'Solicitud no válida',
        invalidBody: 'Vuelve a empezar el proceso para restablecer tu contraseña.',
        invalidCta: 'Ir a recuperar contraseña',
        successTitle: '¡Contraseña restablecida!',
        successBody: 'Tu contraseña se cambió correctamente.',
        redirecting: 'Te llevamos a iniciar sesión...',
        title: 'Restablece tu contraseña',
        subtitle: 'Ingresa el código de verificación que enviamos a',
        codeLabel: 'Código de verificación',
        codeCharAria: 'Carácter {n} de 6',
        noCode: '¿No recibiste el código?',
        resend: 'Reenviar código',
        resendIn: 'Reenviar en {n} s',
        newPassword: 'Nueva contraseña',
        confirmPassword: 'Confirma la nueva contraseña',
        submit: 'Restablecer contraseña',
        submitting: 'Restableciendo contraseña...',
        visualTitle: '¡Ya casi terminas!',
        visualBody: 'Ingresa el código de verificación y crea una contraseña nueva y segura para tu cuenta.',
        tipsTitle: 'Consejos para tu contraseña:',
        tip1: 'Al menos 8 caracteres',
        tip2: 'Combina letras, números y símbolos',
        tip3: 'Evita palabras o patrones comunes',
        errorCodeIncomplete: 'Ingresa el código de verificación de 6 caracteres',
        errorCodeInvalid: 'El código de verificación no es correcto. Revísalo e inténtalo de nuevo.',
        errorCodeExpired: 'El código caducó. Pide uno nuevo con «Reenviar código».',
        errorCodeUsed: 'Este código ya se usó. Pide uno nuevo.',
        errorGeneric: 'No pudimos restablecer tu contraseña. Inténtalo de nuevo.',
        errorResend: 'No pudimos reenviar el código. Inténtalo de nuevo.',
    },
    invite: {
        verifying: 'Verificando la invitación...',
        invalidTitle: 'Invitación no válida',
        goToSignIn: 'Ir a iniciar sesión',
        backToHome: 'Volver al inicio',
        title: 'Aceptar invitación',
        subtitle: 'Completa la configuración de tu cuenta para unirte al equipo',
        invitedToJoin: 'Te invitaron a unirte a',
        invitedBy: 'Invitación de {name}',
        firstName: 'Nombre',
        lastName: 'Apellido',
        email: 'Correo',
        createPassword: 'Crea una contraseña',
        confirmPassword: 'Confirma la contraseña',
        submit: 'Unirme al equipo',
        submitting: 'Creando cuenta...',
        expiresOn: 'Esta invitación caduca el {date}',
        expiresSoon: 'Esta invitación caduca pronto',
        alreadyHaveAccount: '¿Ya tienes una cuenta?',
        visualTitle: '¡Te damos la bienvenida al equipo!',
        visualJoin: 'Estás a punto de unirte a {account} en EZDocuAI.',
        visualSetup: 'Crea tu contraseña para empezar a colaborar en las traducciones.',
        statAiTitle: 'Con IA',
        statAiSub: 'Herramientas de traducción',
        statRealtimeTitle: 'En tiempo real',
        statRealtimeSub: 'Colaboración',
        errorInvalidLink: 'El enlace de invitación no es válido',
        errorNotFound: 'Esta invitación no es válida o ya se usó.',
        errorExpired: 'Esta invitación caducó. Pide a quien te invitó que te envíe una nueva.',
        errorVerify: 'No pudimos verificar la invitación. Inténtalo de nuevo.',
        errorAccept: 'No pudimos aceptar la invitación. Inténtalo de nuevo.',
        errorSession: 'Tu cuenta está lista, pero no pudimos iniciar tu sesión. Inicia sesión.',
    },
    signup: {
        errorCodeExpired: 'El código caducó. Vuelve a registrarte para recibir uno nuevo.',
        errorTooManyAttempts: 'Demasiados intentos incorrectos. Vuelve a registrarte para recibir un código nuevo.',
        errorNoPending: 'No encontramos un registro pendiente con este correo. Vuelve a registrarte.',
        errorFailed: 'No pudimos crear tu cuenta. Inténtalo de nuevo.',
        errorInternalOnly: 'Este entorno es solo para cuentas internas.',
        errorName: 'Revisa el nombre y el apellido: al menos 2 caracteres y sin símbolos.',
        errorCompanyName: 'Revisa el nombre de la empresa: al menos 2 caracteres y sin símbolos.',
        errorGeneric: 'No pudimos completar la solicitud. Inténtalo de nuevo.',
    },
};

const pt: AccountFlowsCopy = {
    shared: {
        language: 'Idioma',
        signIn: 'Entrar',
        backToSignIn: 'Voltar para o login',
        loading: 'Carregando...',
        connectionError: 'Erro de conexão. Tente novamente.',
        tooManyRequests: 'Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.',
        accountDeactivated: 'Sua conta foi desativada. Entre em contato com seu administrador.',
        passwordMismatch: 'As senhas não coincidem',
        passwordMinLength: 'A senha deve ter pelo menos 8 caracteres',
        passwordPolicy: 'A senha deve incluir uma letra maiúscula, uma minúscula e um número, sem espaços.',
        passwordPlaceholder: 'Mín. 8 caracteres',
        confirmPasswordPlaceholder: 'Confirme sua senha',
        showPassword: 'Mostrar senha',
        hidePassword: 'Ocultar senha',
    },
    forgot: {
        title: 'Esqueceu a senha?',
        subtitle: 'Não se preocupe: enviaremos um código de verificação para você redefinir sua senha.',
        emailLabel: 'E-mail',
        emailPlaceholder: 'Digite seu e-mail',
        submit: 'Enviar código de verificação',
        submitting: 'Enviando código...',
        rememberPassword: 'Lembrou sua senha?',
        sentTitle: 'Verifique seu e-mail',
        sentBody: 'Enviamos um código de verificação para',
        redirecting: 'Redirecionando para a página de verificação...',
        visualTitle: 'Redefinição de senha segura',
        visualBody: 'Enviaremos um código seguro para confirmar sua identidade e ajudar você a criar uma nova senha.',
        errorNotFound: 'Não encontramos nenhuma conta com este e-mail.',
        errorInvalidEmail: 'Digite um e-mail válido.',
        errorGeneric: 'Não foi possível enviar o código. Tente novamente.',
    },
    reset: {
        invalidTitle: 'Solicitação inválida',
        invalidBody: 'Comece novamente o processo de redefinição de senha.',
        invalidCta: 'Ir para recuperar senha',
        successTitle: 'Senha redefinida com sucesso!',
        successBody: 'Sua senha foi alterada com sucesso.',
        redirecting: 'Redirecionando para o login...',
        title: 'Redefina sua senha',
        subtitle: 'Digite o código de verificação enviado para',
        codeLabel: 'Código de verificação',
        codeCharAria: 'Caractere {n} de 6',
        noCode: 'Não recebeu o código?',
        resend: 'Reenviar código',
        resendIn: 'Reenviar em {n}s',
        newPassword: 'Nova senha',
        confirmPassword: 'Confirme a nova senha',
        submit: 'Redefinir senha',
        submitting: 'Redefinindo senha...',
        visualTitle: 'Quase lá!',
        visualBody: 'Digite o código de verificação e crie uma nova senha forte para sua conta.',
        tipsTitle: 'Dicas de senha:',
        tip1: 'Pelo menos 8 caracteres',
        tip2: 'Combine letras, números e símbolos',
        tip3: 'Evite palavras ou padrões comuns',
        errorCodeIncomplete: 'Digite o código de verificação de 6 caracteres',
        errorCodeInvalid: 'O código de verificação está incorreto. Confira e tente novamente.',
        errorCodeExpired: 'O código expirou. Solicite um novo em “Reenviar código”.',
        errorCodeUsed: 'Este código já foi usado. Solicite um novo.',
        errorGeneric: 'Não foi possível redefinir sua senha. Tente novamente.',
        errorResend: 'Não foi possível reenviar o código. Tente novamente.',
    },
    invite: {
        verifying: 'Verificando o convite...',
        invalidTitle: 'Convite inválido',
        goToSignIn: 'Ir para o login',
        backToHome: 'Voltar ao início',
        title: 'Aceitar convite',
        subtitle: 'Conclua a configuração da sua conta para entrar na equipe',
        invitedToJoin: 'Convite para entrar em',
        invitedBy: 'Convite de {name}',
        firstName: 'Nome',
        lastName: 'Sobrenome',
        email: 'E-mail',
        createPassword: 'Crie uma senha',
        confirmPassword: 'Confirme a senha',
        submit: 'Entrar na equipe',
        submitting: 'Criando conta...',
        expiresOn: 'Este convite expira em {date}',
        expiresSoon: 'Este convite expira em breve',
        alreadyHaveAccount: 'Já tem uma conta?',
        visualTitle: 'Boas-vindas à equipe!',
        visualJoin: 'Você está prestes a entrar em {account} no EZDocuAI.',
        visualSetup: 'Defina sua senha para começar a colaborar nas traduções.',
        statAiTitle: 'Com IA',
        statAiSub: 'Ferramentas de tradução',
        statRealtimeTitle: 'Em tempo real',
        statRealtimeSub: 'Colaboração',
        errorInvalidLink: 'O link do convite é inválido',
        errorNotFound: 'Este convite é inválido ou já foi usado.',
        errorExpired: 'Este convite expirou. Peça a quem enviou o convite que mande um novo.',
        errorVerify: 'Não foi possível verificar o convite. Tente novamente.',
        errorAccept: 'Não foi possível aceitar o convite. Tente novamente.',
        errorSession: 'Sua conta está pronta, mas não foi possível iniciar sua sessão. Faça login.',
    },
    signup: {
        errorCodeExpired: 'O código expirou. Cadastre-se novamente para receber um novo.',
        errorTooManyAttempts: 'Muitas tentativas incorretas. Cadastre-se novamente para receber um novo código.',
        errorNoPending: 'Não encontramos um cadastro pendente para este e-mail. Cadastre-se novamente.',
        errorFailed: 'Não foi possível criar sua conta. Tente novamente.',
        errorInternalOnly: 'Este ambiente é apenas para contas internas.',
        errorName: 'Confira o nome e o sobrenome: pelo menos 2 caracteres, sem símbolos.',
        errorCompanyName: 'Confira o nome da empresa: pelo menos 2 caracteres, sem símbolos.',
        errorGeneric: 'Não foi possível concluir a solicitação. Tente novamente.',
    },
};

const de: AccountFlowsCopy = {
    shared: {
        language: 'Sprache',
        signIn: 'Anmelden',
        backToSignIn: 'Zurück zur Anmeldung',
        loading: 'Wird geladen...',
        connectionError: 'Verbindungsfehler. Bitte versuchen Sie es erneut.',
        tooManyRequests: 'Zu viele Versuche. Bitte warten Sie ein paar Minuten und versuchen Sie es erneut.',
        accountDeactivated: 'Ihr Konto wurde deaktiviert. Bitte wenden Sie sich an Ihren Administrator.',
        passwordMismatch: 'Die Passwörter stimmen nicht überein',
        passwordMinLength: 'Das Passwort muss mindestens 8 Zeichen lang sein',
        passwordPolicy: 'Das Passwort muss einen Groß- und einen Kleinbuchstaben sowie eine Zahl enthalten, ohne Leerzeichen.',
        passwordPlaceholder: 'Mind. 8 Zeichen',
        confirmPasswordPlaceholder: 'Passwort bestätigen',
        showPassword: 'Passwort anzeigen',
        hidePassword: 'Passwort ausblenden',
    },
    forgot: {
        title: 'Passwort vergessen?',
        subtitle: 'Kein Problem – wir senden Ihnen einen Bestätigungscode, mit dem Sie Ihr Passwort zurücksetzen können.',
        emailLabel: 'E-Mail-Adresse',
        emailPlaceholder: 'Geben Sie Ihre E-Mail-Adresse ein',
        submit: 'Bestätigungscode senden',
        submitting: 'Code wird gesendet...',
        rememberPassword: 'Passwort wieder eingefallen?',
        sentTitle: 'Prüfen Sie Ihr Postfach',
        sentBody: 'Wir haben einen Bestätigungscode an diese Adresse gesendet:',
        redirecting: 'Sie werden zur Bestätigungsseite weitergeleitet...',
        visualTitle: 'Sicheres Zurücksetzen des Passworts',
        visualBody: 'Wir senden Ihnen einen sicheren Code, um Ihre Identität zu bestätigen und Ihnen beim Erstellen eines neuen Passworts zu helfen.',
        errorNotFound: 'Mit dieser E-Mail-Adresse wurde kein Konto gefunden.',
        errorInvalidEmail: 'Geben Sie eine gültige E-Mail-Adresse ein.',
        errorGeneric: 'Der Code konnte nicht gesendet werden. Bitte versuchen Sie es erneut.',
    },
    reset: {
        invalidTitle: 'Ungültige Anfrage',
        invalidBody: 'Bitte starten Sie das Zurücksetzen des Passworts von vorn.',
        invalidCta: 'Zu „Passwort vergessen“',
        successTitle: 'Passwort erfolgreich zurückgesetzt!',
        successBody: 'Ihr Passwort wurde erfolgreich geändert.',
        redirecting: 'Sie werden zur Anmeldung weitergeleitet...',
        title: 'Passwort zurücksetzen',
        subtitle: 'Geben Sie den Bestätigungscode ein, den wir an diese Adresse gesendet haben:',
        codeLabel: 'Bestätigungscode',
        codeCharAria: 'Zeichen {n} von 6',
        noCode: 'Keinen Code erhalten?',
        resend: 'Code erneut senden',
        resendIn: 'Erneut senden in {n} s',
        newPassword: 'Neues Passwort',
        confirmPassword: 'Neues Passwort bestätigen',
        submit: 'Passwort zurücksetzen',
        submitting: 'Passwort wird zurückgesetzt...',
        visualTitle: 'Fast geschafft!',
        visualBody: 'Geben Sie den Bestätigungscode ein und erstellen Sie ein sicheres neues Passwort für Ihr Konto.',
        tipsTitle: 'Tipps für Ihr Passwort:',
        tip1: 'Mindestens 8 Zeichen',
        tip2: 'Eine Mischung aus Buchstaben, Zahlen und Sonderzeichen',
        tip3: 'Vermeiden Sie gängige Wörter oder Muster',
        errorCodeIncomplete: 'Bitte geben Sie den 6-stelligen Bestätigungscode ein',
        errorCodeInvalid: 'Der Bestätigungscode ist falsch. Bitte prüfen Sie ihn und versuchen Sie es erneut.',
        errorCodeExpired: 'Der Code ist abgelaufen. Fordern Sie über „Code erneut senden“ einen neuen an.',
        errorCodeUsed: 'Dieser Code wurde bereits verwendet. Fordern Sie einen neuen an.',
        errorGeneric: 'Ihr Passwort konnte nicht zurückgesetzt werden. Bitte versuchen Sie es erneut.',
        errorResend: 'Der Code konnte nicht erneut gesendet werden. Bitte versuchen Sie es erneut.',
    },
    invite: {
        verifying: 'Einladung wird überprüft...',
        invalidTitle: 'Ungültige Einladung',
        goToSignIn: 'Zur Anmeldung',
        backToHome: 'Zurück zur Startseite',
        title: 'Einladung annehmen',
        subtitle: 'Schließen Sie die Einrichtung Ihres Kontos ab, um dem Team beizutreten',
        invitedToJoin: 'Ihre Einladung zum Team von',
        invitedBy: 'Eingeladen von {name}',
        firstName: 'Vorname',
        lastName: 'Nachname',
        email: 'E-Mail',
        createPassword: 'Passwort erstellen',
        confirmPassword: 'Passwort bestätigen',
        submit: 'Team beitreten',
        submitting: 'Konto wird erstellt...',
        expiresOn: 'Diese Einladung läuft am {date} ab',
        expiresSoon: 'Diese Einladung läuft bald ab',
        alreadyHaveAccount: 'Sie haben bereits ein Konto?',
        visualTitle: 'Willkommen im Team!',
        visualJoin: 'Sie treten gleich {account} auf EZDocuAI bei.',
        visualSetup: 'Legen Sie Ihr Passwort fest, um mit der Zusammenarbeit an Übersetzungen zu beginnen.',
        statAiTitle: 'KI-gestützt',
        statAiSub: 'Übersetzungstools',
        statRealtimeTitle: 'Echtzeit',
        statRealtimeSub: 'Zusammenarbeit',
        errorInvalidLink: 'Der Einladungslink ist ungültig',
        errorNotFound: 'Diese Einladung ist ungültig oder wurde bereits verwendet.',
        errorExpired: 'Diese Einladung ist abgelaufen. Bitten Sie die Person, die Sie eingeladen hat, um eine neue Einladung.',
        errorVerify: 'Die Einladung konnte nicht überprüft werden. Bitte versuchen Sie es erneut.',
        errorAccept: 'Die Einladung konnte nicht angenommen werden. Bitte versuchen Sie es erneut.',
        errorSession: 'Ihr Konto ist eingerichtet, aber die Anmeldung ist fehlgeschlagen. Bitte melden Sie sich an.',
    },
    signup: {
        errorCodeExpired: 'Der Code ist abgelaufen. Registrieren Sie sich erneut, um einen neuen zu erhalten.',
        errorTooManyAttempts: 'Zu viele falsche Versuche. Registrieren Sie sich erneut, um einen neuen Code zu erhalten.',
        errorNoPending: 'Für diese E-Mail-Adresse gibt es keine offene Registrierung. Bitte registrieren Sie sich erneut.',
        errorFailed: 'Ihr Konto konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
        errorInternalOnly: 'Diese Umgebung ist nur für interne Konten.',
        errorName: 'Prüfen Sie Vor- und Nachnamen: mindestens 2 Zeichen, keine Sonderzeichen.',
        errorCompanyName: 'Prüfen Sie den Firmennamen: mindestens 2 Zeichen, keine Sonderzeichen.',
        errorGeneric: 'Die Anfrage konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.',
    },
};

const fr: AccountFlowsCopy = {
    shared: {
        language: 'Langue',
        signIn: 'Se connecter',
        backToSignIn: 'Retour à la connexion',
        loading: 'Chargement...',
        connectionError: 'Erreur de connexion. Veuillez réessayer.',
        tooManyRequests: 'Trop de tentatives. Veuillez patienter quelques minutes avant de réessayer.',
        accountDeactivated: 'Votre compte a été désactivé. Contactez votre administrateur.',
        passwordMismatch: 'Les mots de passe ne correspondent pas',
        passwordMinLength: 'Le mot de passe doit contenir au moins 8 caractères',
        passwordPolicy: 'Le mot de passe doit contenir une majuscule, une minuscule et un chiffre, sans espaces.',
        passwordPlaceholder: 'Min. 8 caractères',
        confirmPasswordPlaceholder: 'Confirmez votre mot de passe',
        showPassword: 'Afficher le mot de passe',
        hidePassword: 'Masquer le mot de passe',
    },
    forgot: {
        title: `Mot de passe oublié${NBSP}?`,
        subtitle: `Pas de souci${NBSP}: nous allons vous envoyer un code de vérification pour réinitialiser votre mot de passe.`,
        emailLabel: 'Adresse e-mail',
        emailPlaceholder: 'Saisissez votre adresse e-mail',
        submit: 'Envoyer le code de vérification',
        submitting: 'Envoi du code...',
        rememberPassword: `Vous vous souvenez de votre mot de passe${NBSP}?`,
        sentTitle: 'Consultez votre boîte de réception',
        sentBody: 'Nous avons envoyé un code de vérification à',
        redirecting: 'Redirection vers la page de vérification...',
        visualTitle: 'Réinitialisation sécurisée du mot de passe',
        visualBody: 'Nous vous enverrons un code sécurisé pour vérifier votre identité et vous aider à créer un nouveau mot de passe.',
        errorNotFound: 'Aucun compte ne correspond à cette adresse e-mail.',
        errorInvalidEmail: 'Saisissez une adresse e-mail valide.',
        errorGeneric: "Nous n'avons pas pu envoyer le code. Veuillez réessayer.",
    },
    reset: {
        invalidTitle: 'Demande non valide',
        invalidBody: 'Veuillez recommencer la procédure de réinitialisation du mot de passe.',
        invalidCta: `Aller à «${NBSP}Mot de passe oublié${NBSP}»`,
        successTitle: `Mot de passe réinitialisé${NBSP}!`,
        successBody: 'Votre mot de passe a bien été modifié.',
        redirecting: 'Redirection vers la connexion...',
        title: 'Réinitialisez votre mot de passe',
        subtitle: 'Saisissez le code de vérification envoyé à',
        codeLabel: 'Code de vérification',
        codeCharAria: 'Caractère {n} sur 6',
        noCode: `Vous n'avez pas reçu le code${NBSP}?`,
        resend: 'Renvoyer le code',
        resendIn: `Renvoyer dans {n}${NBSP}s`,
        newPassword: 'Nouveau mot de passe',
        confirmPassword: 'Confirmez le nouveau mot de passe',
        submit: 'Réinitialiser le mot de passe',
        submitting: 'Réinitialisation en cours...',
        visualTitle: `Vous y êtes presque${NBSP}!`,
        visualBody: 'Saisissez le code de vérification et créez un nouveau mot de passe robuste pour votre compte.',
        tipsTitle: `Conseils pour votre mot de passe${NBSP}:`,
        tip1: 'Au moins 8 caractères',
        tip2: 'Un mélange de lettres, de chiffres et de symboles',
        tip3: 'Évitez les mots ou les suites trop courants',
        errorCodeIncomplete: 'Veuillez saisir le code de vérification à 6 caractères',
        errorCodeInvalid: 'Le code de vérification est incorrect. Vérifiez-le et réessayez.',
        errorCodeExpired: `Le code a expiré. Demandez-en un nouveau avec «${NBSP}Renvoyer le code${NBSP}».`,
        errorCodeUsed: 'Ce code a déjà été utilisé. Demandez-en un nouveau.',
        errorGeneric: "Nous n'avons pas pu réinitialiser votre mot de passe. Veuillez réessayer.",
        errorResend: "Nous n'avons pas pu renvoyer le code. Veuillez réessayer.",
    },
    invite: {
        verifying: "Vérification de l'invitation...",
        invalidTitle: 'Invitation non valide',
        goToSignIn: 'Aller à la connexion',
        backToHome: "Retour à l'accueil",
        title: "Accepter l'invitation",
        subtitle: "Finalisez la configuration de votre compte pour rejoindre l'équipe",
        invitedToJoin: 'Invitation à rejoindre',
        invitedBy: 'Invitation de {name}',
        firstName: 'Prénom',
        lastName: 'Nom',
        email: 'E-mail',
        createPassword: 'Créez un mot de passe',
        confirmPassword: 'Confirmez le mot de passe',
        submit: "Rejoindre l'équipe",
        submitting: 'Création du compte...',
        expiresOn: 'Cette invitation expire le {date}',
        expiresSoon: 'Cette invitation expire bientôt',
        alreadyHaveAccount: `Vous avez déjà un compte${NBSP}?`,
        visualTitle: `Bienvenue dans l'équipe${NBSP}!`,
        visualJoin: 'Vous êtes sur le point de rejoindre {account} sur EZDocuAI.',
        visualSetup: 'Définissez votre mot de passe pour commencer à collaborer sur les traductions.',
        statAiTitle: "Avec l'IA",
        statAiSub: 'Outils de traduction',
        statRealtimeTitle: 'Temps réel',
        statRealtimeSub: 'Collaboration',
        errorInvalidLink: "Le lien d'invitation n'est pas valide",
        errorNotFound: "Cette invitation n'est pas valide ou a déjà été utilisée.",
        errorExpired: "Cette invitation a expiré. Demandez une nouvelle invitation à la personne qui vous l'a envoyée.",
        errorVerify: "Nous n'avons pas pu vérifier l'invitation. Veuillez réessayer.",
        errorAccept: "Nous n'avons pas pu accepter l'invitation. Veuillez réessayer.",
        errorSession: "Votre compte est prêt, mais nous n'avons pas pu vous connecter. Veuillez vous connecter.",
    },
    signup: {
        errorCodeExpired: 'Le code a expiré. Inscrivez-vous de nouveau pour en recevoir un autre.',
        errorTooManyAttempts: 'Trop de tentatives incorrectes. Inscrivez-vous de nouveau pour recevoir un nouveau code.',
        errorNoPending: 'Aucune inscription en attente pour cette adresse e-mail. Veuillez vous inscrire de nouveau.',
        errorFailed: "Nous n'avons pas pu créer votre compte. Veuillez réessayer.",
        errorInternalOnly: 'Cet environnement est réservé aux comptes internes.',
        errorName: 'Vérifiez le prénom et le nom : au moins 2 caractères, sans symboles.',
        errorCompanyName: "Vérifiez le nom de l'entreprise : au moins 2 caractères, sans symboles.",
        errorGeneric: "Nous n'avons pas pu traiter la demande. Veuillez réessayer.",
    },
};

export const ACCOUNT_FLOWS_COPY: Record<Locale, AccountFlowsCopy> = { en, es, pt, de, fr };

export function getAccountFlowsCopy(locale: string | null | undefined): AccountFlowsCopy {
    return (locale && ACCOUNT_FLOWS_COPY[locale as Locale]) || ACCOUNT_FLOWS_COPY.en;
}

/** Rellena `{n}`, `{name}`, `{date}`… Lo que no llega queda vacío. */
export function fmt(texto: string, vars: Record<string, string | number> = {}): string {
    return texto.replace(/\{(\w+)\}/g, (_m, k: string) => (vars[k] == null ? '' : String(vars[k])));
}

/** Parte un texto en lo que va antes y después de `{marcador}` (para poner negrita en medio). */
export function splitAround(texto: string, marcador: string): [string, string] {
    const i = texto.indexOf(`{${marcador}}`);
    if (i < 0) return [texto, ''];
    return [texto.slice(0, i), texto.slice(i + marcador.length + 2)];
}

// ============================================
// Errores del backend → texto del idioma
// ============================================

/**
 * Estado real de una respuesta: auth-ms lo pone en el cuerpo (`status`), el
 * gateway en `statusCode` (validación, límites) y, si el cuerpo no se pudo
 * leer, queda el HTTP.
 */
export function statusOf(httpStatus: number, body: unknown): number {
    const b = body as { status?: unknown; statusCode?: unknown } | null | undefined;
    const s = Number(b?.status ?? b?.statusCode);
    return Number.isFinite(s) && s > 0 ? s : httpStatus;
}

function textoDe(mensaje: unknown): string {
    if (Array.isArray(mensaje)) return mensaje.map(String).join(' ');
    return mensaje == null ? '' : String(mensaje);
}

export function forgotErrorMessage(copy: AccountFlowsCopy, status: number): string {
    if (status === 404) return copy.forgot.errorNotFound;
    if (status === 403) return copy.shared.accountDeactivated;
    if (status === 429) return copy.shared.tooManyRequests;
    if (status === 400) return copy.forgot.errorInvalidEmail;
    return copy.forgot.errorGeneric;
}

export function resetErrorMessage(copy: AccountFlowsCopy, status: number, mensaje?: unknown): string {
    if (status === 429) return copy.shared.tooManyRequests;
    if (status === 403) return copy.shared.accountDeactivated;
    if (status === 400) {
        const t = textoDe(mensaje);
        if (/expired/i.test(t)) return copy.reset.errorCodeExpired;
        if (/already been used/i.test(t)) return copy.reset.errorCodeUsed;
        if (/newPassword|password/i.test(t)) return copy.shared.passwordPolicy;
        return copy.reset.errorCodeInvalid;
    }
    return copy.reset.errorGeneric;
}

export function inviteVerifyErrorMessage(copy: AccountFlowsCopy, status: number): string {
    if (status === 404) return copy.invite.errorNotFound;
    if (status === 410) return copy.invite.errorExpired;
    if (status === 429) return copy.shared.tooManyRequests;
    return copy.invite.errorVerify;
}

export function inviteAcceptErrorMessage(copy: AccountFlowsCopy, status: number, mensaje?: unknown): string {
    if (status === 404) return copy.invite.errorNotFound;
    if (status === 410) return copy.invite.errorExpired;
    if (status === 429) return copy.shared.tooManyRequests;
    if (status === 400) {
        const t = textoDe(mensaje);
        if (/token/i.test(t)) return copy.invite.errorNotFound;
        if (/password/i.test(t)) return copy.shared.passwordPolicy;
    }
    return copy.invite.errorAccept;
}

/**
 * Mensaje en inglés de auth-ms o de la validación del gateway (login,
 * registro, código del registro, olvidé/restablecer contraseña) → texto del
 * idioma. `null` si no se reconoce: quien llama pone un genérico y nunca
 * enseña el inglés. Acepta el array de class-validator.
 */
export function authBackendMessage(copy: AccountFlowsCopy, mensaje: unknown): string | null {
    const lista = Array.isArray(mensaje) ? mensaje : [mensaje];
    const primero = lista[0];
    const t = typeof primero === 'string'
        ? primero
        : primero && typeof primero === 'object' && 'message' in primero ? String((primero as { message: unknown }).message) : '';
    if (!t) return null;
    if (/throttler|too many requests/i.test(t)) return copy.shared.tooManyRequests;
    if (/has been deactivated/i.test(t)) return copy.shared.accountDeactivated;
    if (/no account found/i.test(t)) return copy.forgot.errorNotFound;
    if (/no pending registration/i.test(t)) return copy.signup.errorNoPending;
    if (/too many incorrect attempts/i.test(t)) return copy.signup.errorTooManyAttempts;
    if (/expired\.?\s*please sign up again/i.test(t)) return copy.signup.errorCodeExpired;
    if (/already been used/i.test(t)) return copy.reset.errorCodeUsed;
    if (/verification code has expired/i.test(t)) return copy.reset.errorCodeExpired;
    if (/verification code/i.test(t)) return copy.reset.errorCodeInvalid;
    if (/registration failed/i.test(t)) return copy.signup.errorFailed;
    if (/cuentas internas|internal accounts/i.test(t)) return copy.signup.errorInternalOnly;
    if (/valid email/i.test(t)) return copy.forgot.errorInvalidEmail;
    if (/password must be at least/i.test(t)) return copy.shared.passwordMinLength;
    if (/password/i.test(t)) return copy.shared.passwordPolicy;
    if (/first name|last name/i.test(t)) return copy.signup.errorName;
    if (/account name/i.test(t)) return copy.signup.errorCompanyName;
    return null;
}

// ============================================
// Petición con respaldo
// ============================================

/**
 * POST JSON a un endpoint de cuenta mandando `locale`. Si el backend todavía
 * no lo admite (forbidNonWhitelisted → 400 «property locale should not
 * exist»), repite la petición sin él: el flujo nunca se rompe por el orden de
 * despliegue.
 */
export async function postAccountFlow(
    url: string,
    payload: Record<string, unknown>,
    fetchImpl: typeof fetch = fetch,
): Promise<{ status: number; data: any }> {
    const enviar = async (cuerpo: Record<string, unknown>) => {
        const res = await fetchImpl(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(cuerpo),
        });
        const data = await res.json().catch(() => null);
        return { status: statusOf(res.status, data), data };
    };
    const primero = await enviar(payload);
    if (primero.status === 400 && 'locale' in payload && /\blocale\b/.test(textoDe(primero.data?.message))) {
        const { locale: _omitido, ...sinLocale } = payload;
        return enviar(sinLocale);
    }
    return primero;
}

// ============================================
// Contraseña
// ============================================

/** La misma regla que el gateway (PASSWORD_REGEX): así su inglés nunca llega. */
export const PASSWORD_POLICY = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)\S{8,128}$/;

export function passwordProblem(copy: AccountFlowsCopy, password: string, confirm: string): string | null {
    if (password !== confirm) return copy.shared.passwordMismatch;
    if (password.length < 8) return copy.shared.passwordMinLength;
    if (!PASSWORD_POLICY.test(password)) return copy.shared.passwordPolicy;
    return null;
}

// ============================================
// Fecha y enlaces
// ============================================

/** «15 de octubre de 2026», «October 15, 2026»…; null si la fecha no sirve. */
export function formatInviteExpiry(locale: Locale, iso: string | null | undefined): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(d);
}

/**
 * Enlace de la misma pantalla en cada idioma, conservando la consulta
 * (`?token=…`, `?email=…`), para el selector de idioma.
 */
export function buildLocaleHrefs(path: string, query: string): Record<Locale, string> {
    const q = query ? `?${query.replace(/^\?/, '')}` : '';
    const limpio = path.startsWith('/') ? path : `/${path}`;
    return Object.fromEntries(ACCOUNT_FLOW_LOCALES.map((l) => [l, `/${l}${limpio}${q}`])) as Record<Locale, string>;
}
